'use client';

import { SUBSCRIPTION_LISTS, SUBSCRIPTION_REQUEST_HEADING, SUBSCRIPTION_PRIOR_UNSUBSCRIBE_NOTE } from '@/lib/subscriptionConsent'

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaTimes, FaAndroid } from 'react-icons/fa';
import { captureEmail } from '@/utils/captureEmail';
import { track } from '@/utils/analytics';
import Confetti from './Confetti';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Stable source label so we can break down waitlist conversion by surface
   *  (home_hero, home_cta_section, scanner_page, blog_inline, etc). */
  source?: string;
}

export default function WaitlistModal({ isOpen, onClose, source = 'unknown' }: WaitlistModalProps) {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const requestRef = useRef(0);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  // Fire `android_waitlist_open` once per open transition. Useful funnel
  // signal — the open/submit ratio tells us whether the form itself is the
  // drop-off, vs. the CTA copy that triggers it.
  useEffect(() => {
    if (isOpen) {
      startedRef.current = false;
      track('android_waitlist_open', { cta_surface: source, form_variant: 'modal', method_version: 'subscription_request_v1' });
      // Move focus into the form on open (accessibility + faster completion).
      const t = setTimeout(() => emailInputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen, source]);

  // Keep keyboard navigation within the dialog and restore the trigger on close.
  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
      if (e.key !== 'Tab') return;
      const nodes = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), a[href]');
      if (!nodes?.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!dialogRef.current?.contains(document.activeElement)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      requestRef.current += 1;
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  const [formData, setFormData] = useState({
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'unknown'>('idle');

  // Reset transient state whenever the modal is (re)opened, so a returning
  // user never sees a stale success/error panel from a previous session.
  useEffect(() => {
    if (isOpen) {
      // This one IS the reset-on-prop-change pattern the rule targets. The
      // idiomatic fix is a `key` on the modal at each call site, which is a
      // parent-side change and out of scope for a framework upgrade. Left as-is
      // deliberately; revisit when touching the modal's callers.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSubmitStatus('idle');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const request = ++requestRef.current;
    track('android_waitlist_attempt', { cta_surface: source, form_variant: 'modal', method_version: 'subscription_request_v1' });
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const emailParams = {
      from_name: '',
      reply_to: formData.email,
      subject: '',
      message: '',
      type: 'android_waitlist' as const
    };

    try {
      const result = await captureEmail({
        email: formData.email.trim(),
        list: 'android_waitlist',
        source: 'android_waitlist_modal',
        context: source,
        emailParams,
      });

      const properties = { cta_surface: source, form_variant: 'modal', method_version: 'subscription_request_v1' };
      if (result.accepted) {
        track('android_waitlist_submit', properties);
        if (request === requestRef.current) {
          setSubmitStatus('success');
          setFormData({ email: '' });
        }
      } else {
        track(result.pending ? 'android_waitlist_unknown' : 'android_waitlist_error', { ...properties, failure_stage: 'delivery' });
        if (request === requestRef.current) setSubmitStatus(result.pending ? 'unknown' : 'error');
      }
    } catch {
      track('android_waitlist_error', { cta_surface: source, form_variant: 'modal', method_version: 'subscription_request_v1', failure_stage: 'delivery' });
      if (request === requestRef.current) setSubmitStatus('error');
    } finally {
      if (request === requestRef.current) setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track('android_waitlist_start', { cta_surface: source, form_variant: 'modal', method_version: 'subscription_request_v1' });
    }
    if (submitStatus === 'error' || submitStatus === 'unknown') setSubmitStatus('idle');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isSuccess = submitStatus === 'success';

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="waitlist-modal-title"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 24 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="relative overflow-hidden bg-white rounded-t-2xl sm:rounded-3xl shadow-2xl shadow-charcoal/25 w-full max-w-md max-h-[90dvh] overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))] ring-1 ring-black/5"
          >
            {/* Top accent bar — subtle Android-green → brand-orange gradient so
                the modal reads as "flashier" without cartoonish noise. */}
            <div
              aria-hidden="true"
              className="h-1.5 w-full bg-gradient-to-r from-[#3DDC84] via-[#1E6B47] to-[#155235]"
            />

            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 tap-target inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6B47]"
              aria-label="Close waitlist dialog"
            >
              <FaTimes className="w-5 h-5" aria-hidden="true" />
            </button>

            <div className="p-6 md:p-8">
              {isSuccess ? (
                // ── Celebration success state ────────────────────────────────
                <div className="relative py-6 text-center" role="status" aria-live="polite">
                  <Confetti />
                  <motion.div
                    initial={{ scale: 0, rotate: -25 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 15 }}
                    className="relative mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#3DDC84]/15 ring-8 ring-[#3DDC84]/10"
                  >
                    <motion.span
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.16, type: 'spring', stiffness: 320, damping: 14 }}
                    >
                      <FaCheck className="h-7 w-7 text-[#25a35c]" aria-hidden="true" />
                    </motion.span>
                  </motion.div>
                  <motion.h2
                    id="waitlist-modal-title"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 }}
                    className="text-2xl font-bold text-gray-900"
                  >
                    {SUBSCRIPTION_REQUEST_HEADING}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.22 }}
                    className="mx-auto mt-2 max-w-xs text-gray-600 leading-relaxed"
                  >
                    {SUBSCRIPTION_PRIOR_UNSUBSCRIBE_NOTE}
                  </motion.p>
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35 }}
                    onClick={onClose}
                    className="mt-6 inline-flex items-center justify-center rounded-control bg-charcoal px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-charcoal/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6B47] focus-visible:ring-offset-2 pressable"
                  >
                    Done
                  </motion.button>
                </div>
              ) : (
                // ── Form state ───────────────────────────────────────────────
                <>
                  <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#3DDC84]/12 px-3 py-1 text-2xs font-semibold uppercase tracking-[0.16em] text-[#1f9553]">
                    <FaAndroid className="w-3.5 h-3.5" aria-hidden="true" />
                    Android · Early access
                  </span>
                  <h2
                    id="waitlist-modal-title"
                    className="font-display text-2xl font-bold mb-2 text-ink pr-10"
                  >
                    Be first on Android
                  </h2>
                  <p className="text-gray-600 mb-6 text-base leading-relaxed">
                    Join the waitlist and we&apos;ll send you a single launch-day
                    email the moment the Android app goes live. No spam, ever.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="waitlist-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        ref={emailInputRef}
                        disabled={isSubmitting}
                        onInvalid={(e) => track('android_waitlist_validation_error', {
                          cta_surface: source, form_variant: 'modal', method_version: 'subscription_request_v1', validation_field: 'email',
                          validation_reason: e.currentTarget.validity.valueMissing ? 'required' : 'format',
                        })}
                        type="email"
                        id="waitlist-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        inputMode="email"
                        className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1E6B47] focus:border-transparent transition-[color,background-color,border-color,text-decoration-color] duration-200"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ y: isSubmitting ? 0 : -2 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                      className={`w-full bg-gradient-to-r from-[#1E6B47] to-[#155235] text-white min-h-[48px] py-3 px-6 rounded-xl shadow-lg shadow-[#1E6B47]/30 hover:shadow-xl hover:shadow-[#1E6B47]/40 transition-shadow duration-200 flex items-center justify-center gap-2 font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6B47] focus-visible:ring-offset-2 ${
                        isSubmitting ? 'opacity-80 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" />
                          <span>Joining…</span>
                        </>
                      ) : (
                        <span>Join the Waitlist</span>
                      )}
                    </motion.button>
                    {(submitStatus === 'error' || submitStatus === 'unknown') && (
                      <p className="text-red-500 text-sm text-center" role="alert">
                        {submitStatus === 'unknown' ? 'We could not confirm yet. Your signup may still arrive; you can retry with the same email.' : 'We could not confirm your signup. Please try again.'}
                      </p>
                    )}
                    <p className="text-center text-xs text-gray-400">
                      {SUBSCRIPTION_LISTS.android_waitlist.consent}{' '}
                      <a href="/privacy" className="underline text-gray-600">Privacy</a>
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
